import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { type Observable, of, switchMap } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { ExportParticipation } from '../use-case-entities/export-participations.js';
import { withIdTiebreaker } from '../util.js';

export const subscribeExportDataView = registerSubscription({
  operationName: 'subscribeExportDataView',
  type: () => [ExportParticipation],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_EXPORTS, params.filter.institutionID),
    ),
  ],
  resolve:
    (skip: number, limit: number, filter: BHFilter) =>
    (gqlFields: BaseQLSelectionSet<ExportParticipation>): Observable<ExportParticipation[]> => {
      if (!filter.institutionID) return of([] as ExportParticipation[]);
      return watchEntireCollection(Participation._collectionName).pipe(
        switchMap(() => {
          const col = mongo().collection<EntityData<Participation>>(Participation._collectionName);
          return col.raw
            .aggregate([
              {
                $replaceRoot: {
                  newRoot: { participation: '$$ROOT', _class: ExportParticipation._class, _id: '$$ROOT._id' },
                },
              },
              {
                $lookup: {
                  from: UserStammdaten._collectionName,
                  let: { userID: '$participation.userID' },
                  pipeline: [
                    {
                      $match: {
                        $expr: {
                          $and: [{ $eq: ['$$userID', '$userID'] }, { $eq: ['$institutionID', filter.institutionID] }],
                        },
                      },
                    },
                  ],
                  as: 'stammdaten',
                },
              },
              { $set: { stammdaten: { $arrayElemAt: ['$stammdaten', 0] } } },
              {
                $lookup: {
                  from: User._collectionName,
                  as: 'user',
                  localField: 'participation.userID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$user' },
              {
                $lookup: {
                  from: Product._collectionName,
                  as: 'product',
                  localField: 'participation.productID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$product' },
              { $match: { $and: [filter.getMongoFilter(), { 'product.institutionID': filter.institutionID }] } },
              { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray() as Promise<ExportParticipation[]>;
        }),
      );
    },
});
